<script lang="ts">
  import { onMount } from "svelte"
  import type {
      InboxWorkflowCustomStepProps,
      InboxWorkflowRowControls,
  } from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import TableStepLayout from "@kontextwerk/web-sdk/ui/layout/TableStepLayout.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiArrowRight } from "@mdi/js"

  import {
    fetchWorkOrderPrintBatchJob,
    type WorkOrderPrintJobStatus,
  } from "../../../../../../../../../../../../api/kehrblatt/letters"
  import { buildInboxCustomStepRowPresentations } from "../../../../_shared/_logic/custom-step-rows"
  import DownloadLetterPrintJobAction from "./_modules/download-letter-print-job-action"
  import type { DownloadLetterPrintJobResult } from "./actions/downloadLetterPrintJob"
  import { buildDownloadLetterPrintJobIntent } from "./build-download-letter-print-job-intent"
  import { downloadLetterPrintJobOperations } from "./operations/03-deliver-appointment-letters-actions-download-letter-print-job"
  import StartLetterPrintJobAction from "./_modules/start-letter-print-job-action"
  import type { StartLetterPrintJobResult } from "./actions/startLetterPrintJob"
  import { buildStartLetterPrintJobIntent } from "./_actions/buildStartLetterPrintJobIntent"
  import { startLetterPrintJobOperations } from "./operations/03-deliver-appointment-letters-actions-start-letter-print-job"
  import BackgroundJobProgressCard from "../../../../../../../../../../_shared/_modules/background-job-progress-card"
  import PrintSelectionPreviewTable from "./_modules/print-selection-preview-table"
  import {
    completionByActionCaseId,
    distinctPersistedPrintJobIds,
    letterDeliveryCompletionsFromWorkflow,
    projectCompletedPrintSelectionRows,
  } from "./_logic/completionOutput"
  import {
    parsePrintSelectionPreviewResult,
    type PrintSelectionPreviewRow,
  } from "../../../../_shared/_logic/print-selection-preview"
  import { hasSamePrintSelection } from "./_logic/printJobSelection"
  import { projectPrintJobProgress } from "./_logic/printJobProgress"
  import {
    initialPrintSelection,
    selectedPrintableItemIds,
    type PrintSelectionState,
    type PrintSelectionValue,
  } from "./_shared/_logic/print-selection-state"
  import {
    applyPrintSelectionPreconfiguration,
    type PrintSelectionPreconfigurationMode,
  } from "./_shared/_logic/print-selection-preconfiguration"
  import { printSelectionEnabledRows, selectedCompletionRows } from "./_logic/rowInteraction"
  import PrintSelectionPreconfiguration from "./_modules/print-selection-preconfiguration"
  import type { PreconfigurationChoice } from "../../../../../../../../../../_shared/_logic/preconfiguration-keep"

  let {
    workflow,
    step,
    organizationId = null,
    readOnly = false,
    rowOutcomes = {},
    showResult,
    commit,
    complete,
    loadPreview = undefined,
    refreshAfterAction,
    help = undefined,
    extra = undefined,
  }: InboxWorkflowCustomStepProps = $props()

  let activeJobId = $state<string | null>(null)
  let activeJobItemIds = $state<string[]>([])
  let status = $state<WorkOrderPrintJobStatus | null>(null)
  let startPhase = $state<"idle" | "starting">("idle")
  let downloading = $state(false)
  let delivering = $state(false)
  let refreshFailed = $state(false)
  let previewLoading = $state(true)
  let previewRows = $state<PrintSelectionPreviewRow[]>([])
  let selection = $state<PrintSelectionState>({})
  let selectedTableRowIds = $state<string[]>([])
  let locallyRemovedActionCaseIds = $state<string[]>([])
  let disposed = false

  const rowControls = $derived((extra?.rowControls ?? null) as InboxWorkflowRowControls | null)
  const rowPresentations = $derived(
    buildInboxCustomStepRowPresentations({
      workflow,
      step,
      rowOutcomes,
      rowControls,
    }),
  )
  const engineRemovedActionCaseIds = $derived(
    Object.values(rowPresentations)
      .filter((presentation) => presentation.removedCurrent)
      .map((presentation) => presentation.actionCaseId),
  )
  const removedActionCaseIds = $derived(
    Array.from(new Set([...engineRemovedActionCaseIds, ...locallyRemovedActionCaseIds])),
  )
  const removedActionCaseIdSet = $derived(new Set(removedActionCaseIds))
  const resolvedOrganizationId = $derived(
    organizationId ?? workflow.items[0]?.action_case.organization_id ?? "",
  )
  const previewActionCaseIds = $derived(
    workflow.items
      .filter((item) => {
        const actionCaseId = String(item.action_case.id ?? "").trim()

        if (!actionCaseId || !item.compatible) return false

        return !rowPresentations[actionCaseId]?.removedEarlier
      })
      .map((item) => String(item.action_case.id)),
  )
  const workflowCompletions = $derived(letterDeliveryCompletionsFromWorkflow(workflow))
  const completions = $derived(workflowCompletions)
  const completionsByActionCaseId = $derived(completionByActionCaseId(completions))
  const persistedPrintJobIds = $derived(distinctPersistedPrintJobIds(completions))
  const displayedRows = $derived(
    projectCompletedPrintSelectionRows(previewRows, completionsByActionCaseId).map((row) => {
      const actionCaseId = String(row.action_case_id ?? "").trim()
      const presentation = actionCaseId ? rowPresentations[actionCaseId] : null

      return presentation ? { ...row, number: presentation.rowNumber } : row
    }),
  )
  const activeDisplayedRows = $derived(
    displayedRows.filter((row) => {
      const actionCaseId = String(row.action_case_id ?? "").trim()

      return !actionCaseId || !removedActionCaseIdSet.has(actionCaseId)
    }),
  )
  const printEnabledRows = $derived(
    printSelectionEnabledRows({
      rows: activeDisplayedRows,
      rowPresentations,
    }),
  )
  const printEnabledItemIds = $derived(
    printEnabledRows.map((row) => row.letter_delivery_batch_item_id),
  )
  const printEnabledItemIdSet = $derived(new Set(printEnabledItemIds))
  const selectedItemIds = $derived(selectedPrintableItemIds(printEnabledRows, selection))
  const selectedPrintRows = $derived.by(() => {
    const selected = new Set(selectedItemIds)

    return printEnabledRows.filter((row) => selected.has(row.letter_delivery_batch_item_id))
  })
  const completionRows = $derived(
    selectedCompletionRows({
      selectedPrintRows,
      rowPresentations,
      completionsByActionCaseId,
      workflowReadOnly: readOnly,
    }),
  )
  const hasPreviewRows = $derived(displayedRows.length > 0)
  const hasRowsOrCompletions = $derived(hasPreviewRows || completions.length > 0)
  const activeJobStatus = $derived(status?.job_id === activeJobId ? status : null)
  const isFinished = $derived(activeJobStatus?.status === "finished")
  const hasFailed = $derived(
    activeJobStatus?.status === "failed" || activeJobStatus?.status === "expired",
  )
  const activeJobMatchesSelection = $derived(
    Boolean(activeJobId && hasSamePrintSelection(activeJobItemIds, selectedItemIds)),
  )
  const displayedActiveJobId = $derived(activeJobMatchesSelection ? activeJobId : null)
  const displayedStatus = $derived(activeJobMatchesSelection ? activeJobStatus : null)
  const displayedProgress = $derived(
    projectPrintJobProgress(displayedStatus, selectedItemIds.length),
  )
  const starting = $derived(startPhase === "starting")
  const committingDelivery = $derived(downloading || delivering)
  const startIntent = $derived(
    buildStartLetterPrintJobIntent({
      organizationId: resolvedOrganizationId,
      itemIds: selectedItemIds,
      activeJobId,
      activeJobItemIds,
      failed: hasFailed,
    }),
  )
  const activeDownloadIntent = $derived(
    buildDownloadLetterPrintJobIntent({
      jobId: displayedActiveJobId,
      organizationId: resolvedOrganizationId,
      available: activeJobMatchesSelection && isFinished && !hasFailed,
      completeDelivery: false,
    }),
  )
  const resetActivePrintJob = () => {
    activeJobId = null
    activeJobItemIds = []
    status = null
  }

  const loadSourcePreview = async () => {
    if (!resolvedOrganizationId) {
      previewLoading = false

      return
    }

    previewLoading = true
    try {
      if (!loadPreview) throw new Error("Preview-Adapter fehlt.")

      const result = await loadPreview({
        action_case_ids: previewActionCaseIds,
        payload: {},
      })
      const parsed = parsePrintSelectionPreviewResult(result)
      previewRows = parsed.rows
      selection = initialPrintSelection(
        projectCompletedPrintSelectionRows(parsed.rows, completionsByActionCaseId),
      )
      selectedTableRowIds = []
      locallyRemovedActionCaseIds = []
      resetActivePrintJob()
    } catch (error) {
      console.error("letter_delivery_print_selection_preview_failed", error)
      newNotification({ class: "error", html: "Druckauswahl konnte nicht geladen werden." })
    } finally {
      previewLoading = false
    }
  }

  const updateSelection = (itemId: string, value: PrintSelectionValue) => {
    if (!printEnabledItemIdSet.has(itemId) || starting || committingDelivery) return

    selection = { ...selection, [itemId]: value }
    resetActivePrintJob()
  }

  const applyPreconfiguration = (
    rows: PrintSelectionPreviewRow[],
    mode: PreconfigurationChoice<PrintSelectionPreconfigurationMode>,
  ) => {
    if (previewLoading || starting || committingDelivery) return

    const result = applyPrintSelectionPreconfiguration({
      selection,
      rows,
      mode,
    })

    if (!result.changed) return

    selection = result.selection
    resetActivePrintJob()
  }

  const resetRows = (rows: PrintSelectionPreviewRow[]) => {
    if (readOnly || !rows.length) return

    const defaults = initialPrintSelection(rows)
    selection = {
      ...selection,
      ...defaults,
    }
    selectedTableRowIds = []
    resetActivePrintJob()
  }

  const removeRows = (rows: PrintSelectionPreviewRow[]) => {
    if (readOnly || !rows.length) return

    const actionCaseIds = rows.map((row) => String(row.action_case_id ?? "").trim()).filter(Boolean)

    if (!actionCaseIds.length) return

    locallyRemovedActionCaseIds = Array.from(
      new Set([...locallyRemovedActionCaseIds, ...actionCaseIds]),
    )
    selectedTableRowIds = []
    resetActivePrintJob()
  }

  const showResultAfterRemoval = () => {
    rowControls?.removeActionCasesFromCurrentStep(locallyRemovedActionCaseIds)
    locallyRemovedActionCaseIds = []
    showResult()
  }

  const restoreRow = (row: PrintSelectionPreviewRow) => {
    if (readOnly) return

    const actionCaseId = String(row.action_case_id ?? "").trim()

    if (!actionCaseId) return

    locallyRemovedActionCaseIds = locallyRemovedActionCaseIds.filter((id) => id !== actionCaseId)

    if (rowPresentations[actionCaseId]?.removedCurrent) {
      rowControls?.restoreActionCasesForCurrentStep([actionCaseId])
    }

    selectedTableRowIds = selectedTableRowIds.filter(
      (id) => id !== row.letter_delivery_batch_item_id,
    )
    resetActivePrintJob()
  }

  const pollStatus = async (jobId: string) => {
    const next = await fetchWorkOrderPrintBatchJob(jobId, resolvedOrganizationId)

    if (activeJobId === jobId) status = next

    return next
  }

  const pollUntilDone = async (jobId: string) => {
    while (!disposed && activeJobId === jobId) {
      let next: WorkOrderPrintJobStatus
      try {
        next = await pollStatus(jobId)
      } catch (error) {
        console.error("letter_delivery_manual_print_job_poll_failed", error)
        newNotification({
          class: "error",
          html: "Status des Zustell-Druckauftrags konnte nicht aktualisiert werden.",
        })

        return
      }

      if (next.status === "finished" || next.status === "failed" || next.status === "expired")
        return

      await new Promise((resolve) => window.setTimeout(resolve, 1000))
    }
  }

  const handleJobStarted = (result: StartLetterPrintJobResult) => {
    activeJobId = result.status.job_id
    activeJobItemIds = [...result.itemIds]
    status = result.status
    void pollUntilDone(result.status.job_id)
  }

  const refreshCompletedDownload = async () => {
    refreshFailed = false
    try {
      await refreshAfterAction()

      if (!disposed) await loadSourcePreview()
    } catch (error) {
      refreshFailed = true
      throw error
    }
  }

  const handleDownloadCompleted = async (_result: DownloadLetterPrintJobResult) => {
    newNotification({
      class: "success",
      html: "PDF heruntergeladen. Bitte die Briefe zustellen und die Zustellung anschließend markieren.",
    })
  }

  const markDelivered = async () => {
    if (readOnly || committingDelivery || !completionRows.length) return

    delivering = true
    let saved = false
    try {
      const result = await commit(
        completionRows.map((row) => ({
          action_case_id: String(row.action_case_id),
          payload: {
            letter_delivery_batch_item_id: row.letter_delivery_batch_item_id,
            print_job_id: displayedActiveJobId ?? undefined,
          },
        })),
      )
      saved = true
      await complete(result)

      if (!disposed) await loadSourcePreview()
    } catch (error) {
      console.error("manual_letter_delivery_commit_failed", error)
      refreshFailed = saved
      newNotification({
        class: "error",
        html: saved
          ? "Die Zustellung wurde gespeichert. Bitte die Anzeige erneut laden."
          : "Die Zustellung konnte nicht gespeichert werden. Bitte erneut versuchen.",
      })
    } finally {
      delivering = false
    }
  }

  onMount(() => {
    void loadSourcePreview()

    return () => {
      disposed = true
    }
  })
</script>

{#snippet preconfiguration()}
  <PrintSelectionPreconfiguration
    rows={printEnabledRows}
    selectedIds={selectedTableRowIds}
    disabled={previewLoading || starting || committingDelivery}
    onApply={applyPreconfiguration}
  />
{/snippet}

{#snippet deliveryAction()}
  {#if completionRows.length && !readOnly}
    <div class="step-actions delivery-action">
      <CTA
        type="primaryButton"
        content={`${completionRows.length} als zugestellt markieren und weiter`}
        disabled={committingDelivery || starting}
        onClick={() => void markDelivered()}
      />
    </div>
  {/if}
{/snippet}

<TableStepLayout
  title={step.label}
  darkMode={true}
  count={previewLoading ? null : displayedRows.length}
  helpModalTextId={help?.textId ?? ""}
  helpModalContext={help?.context}
  helpModalFallback={help?.fallback ?? undefined}
  helpModalTooltip={help?.tooltip}
  showPreconfiguration={true}
  showFooter={completionRows.length > 0 && !readOnly}
  titleActions={completionRows.length > 0 && !readOnly ? deliveryAction : null}
  footer={deliveryAction}
  {preconfiguration}
>
  <div class="manual-download-step">
    {#if refreshFailed}
      <div role="status">
        <p>
          Die Briefe wurden abgeschlossen. Die Anzeige konnte nicht aktualisiert werden. Bitte
          erneut laden.
        </p>
        <CTA
          type="secondaryButton"
          content="Erneut laden"
          small={true}
          onClick={() => void refreshCompletedDownload().catch(() => {})}
        />
      </div>
    {/if}
    {#if previewLoading || hasRowsOrCompletions}
      {#if printEnabledRows.length}
        <div class="render-card">
          <div>
            <p class="eyebrow">Terminbriefe rendern</p>
            <h3>
              {selectedItemIds.length} Terminbrief{selectedItemIds.length === 1 ? "" : "e"} ausgewählt
            </h3>
            <p>Der Druckauftrag wird erst gestartet, wenn Sie ihn hier auslösen.</p>
          </div>
          <StartLetterPrintJobAction
            intent={startIntent}
            operations={startLetterPrintJobOperations}
            onSubmissionResult={handleJobStarted}
            onPhaseChange={(phase) => (startPhase = phase)}
          />
        </div>
      {/if}

      {#if printEnabledRows.length && displayedActiveJobId}
        <BackgroundJobProgressCard view={displayedProgress} />
        {#if completionRows.length}
          <p>
            Das PDF ist zum Ausdrucken. Markieren Sie die Briefe erst als zugestellt, nachdem Sie
            sie eingeworfen oder übergeben haben.
          </p>
        {/if}
        <div class="step-actions">
          <DownloadLetterPrintJobAction
            intent={activeDownloadIntent}
            operations={downloadLetterPrintJobOperations}
            onSubmissionResult={handleDownloadCompleted}
            onPendingChange={(pending) => (downloading = pending)}
          />
        </div>
      {/if}

      {#if completions.length}
        <div class="summary-card">
          <p class="eyebrow">Briefe erledigt</p>
          <h3>{completions.length} Terminbrief{completions.length === 1 ? "" : "e"} dokumentiert</h3
          >
        </div>
        {#if persistedPrintJobIds.length}
          <div class="step-actions">
            {#each persistedPrintJobIds as printJobId, index}
              <DownloadLetterPrintJobAction
                intent={buildDownloadLetterPrintJobIntent({
                  jobId: printJobId,
                  organizationId: resolvedOrganizationId,
                  available: true,
                  label:
                    persistedPrintJobIds.length === 1
                      ? "PDF erneut herunterladen"
                      : `PDF ${index + 1} erneut herunterladen`,
                })}
                operations={downloadLetterPrintJobOperations}
                onSubmissionResult={handleDownloadCompleted}
              />
            {/each}
          </div>
        {/if}
      {/if}
    {:else}
      <div class="summary-card">
        <p class="eyebrow">Keine Terminbriefe</p>
        <h3>Für diese Auswahl gibt es keine druckbaren Terminbriefe.</h3>
      </div>
      <div class="step-actions">
        <CTA type="primaryButton" content="Zum Ergebnis" onClick={showResult}>
          {#snippet iconSnippet()}<Icon path={mdiArrowRight} />{/snippet}
        </CTA>
      </div>
    {/if}

    {#if !readOnly && displayedRows.length && activeDisplayedRows.every((row) => completionsByActionCaseId[String(row.action_case_id ?? "")])}
      <div class="step-actions">
        <CTA
          type="secondaryButton"
          content="Zum Ergebnis"
          small={true}
          onClick={showResultAfterRemoval}
        />
      </div>
    {/if}
    <PrintSelectionPreviewTable
      rows={displayedRows}
      {selection}
      {rowPresentations}
      {removedActionCaseIds}
      printSelectionEnabledItemIds={printEnabledItemIds}
      selectedIds={selectedTableRowIds}
      workflowActionsEnabled={Boolean(rowControls) && !readOnly}
      workflowReadOnly={readOnly}
      loading={previewLoading}
      disabled={starting || committingDelivery}
      onChange={updateSelection}
      onSelectionChange={(ids) => (selectedTableRowIds = ids)}
      onResetRows={resetRows}
      onRemoveRows={removeRows}
      onRestoreRow={restoreRow}
    />
  </div>
</TableStepLayout>

<style lang="less">
  .manual-download-step {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .summary-card,
  .render-card {
    border: 1px solid color-mix(in srgb, var(--text-invers-100) 18%, transparent);
    padding: 0.9rem;
    color: var(--text-invers-100);
  }

  .render-card {
    align-items: center;
    display: flex;
    gap: 1rem;
    justify-content: space-between;
  }

  .summary-card p,
  .summary-card h3,
  .render-card p,
  .render-card h3 {
    margin: 0;
  }

  .summary-card h3,
  .render-card h3 {
    margin-top: 0.15rem;
  }

  .render-card > div {
    min-width: 0;
  }

  .eyebrow {
    font-size: 0.72rem;
    text-transform: uppercase;
    font-weight: 900;
    color: var(--primary-100);
  }

  .step-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.65rem;
  }

  .delivery-action {
    min-width: 0;
    width: 100%;
  }

  .delivery-action :global(button.cta) {
    max-width: 100%;
  }

  .delivery-action :global(button.cta p) {
    white-space: normal;
    overflow-wrap: anywhere;
  }
</style>
